import moment from "moment";
import { i18n } from "@/i18n";
import { ChatLinkType } from "@/models/enums/chat";
import type { Bill, Link } from "@/store/chats";

let cachedSearchText: string | null = null;
let cachedHighlightRegex: RegExp | null = null;

function getHighlightRegex(searchText: string): RegExp | null {
    if (searchText !== cachedSearchText) {
        cachedSearchText = searchText;
        const keywords = searchText.split(' ').filter(word => word.length > 0);
        if (keywords.length === 0) {
            cachedHighlightRegex = null;
        } else {
            const escapedKeywords = keywords.map(word =>
                word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            );
            cachedHighlightRegex = new RegExp(`(${escapedKeywords.join('|')})`, "gi");
        }
    }
    return cachedHighlightRegex;
}

export function highlightSearchText(searchText: string, text: string): string {
    if (!text || !searchText) {
        return text;
    }

    const highlightRegex = getHighlightRegex(searchText);
    if (!highlightRegex) {
        return text;
    }
    return text.replace(highlightRegex, "<span class='searchedText'>$1</span>");
}

// Подсветка сырого текста для v-html: совпадения ищутся в исходной строке, а каждый
// кусок экранируется отдельно. В отличие от highlightSearchText(escapeHtml(...)) поиск
// по "a" или "amp" не разрывает сущности вроде &amp; в тексте с "&".
export function highlightSearchHtml(searchText: string, text: string): string {
    if (!text) {
        return '';
    }
    const highlightRegex = searchText ? getHighlightRegex(searchText) : null;
    if (!highlightRegex) {
        return escapeHtml(text);
    }
    // единственная группа захвата в регулярке кладёт совпадения на нечётные индексы
    return text
        .split(highlightRegex)
        .map((part, i) => (i % 2 === 1
            ? `<span class='searchedText'>${escapeHtml(part)}</span>`
            : escapeHtml(part)))
        .join('');
}

export function highlightFileSearchText(searchText: string, text: string): string {
    if (!text || !searchText) {
        return text;
    }

    const keywords = searchText
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (keywords.length === 0) {
        return text;
    }

    const escapedKeywords = keywords.map(word =>
        word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const highlightRegex = new RegExp(`(${escapedKeywords.join('|')})`, "gi");

    return text.replace(highlightRegex, "<span class='searchedText'>$1</span>");
}

// Экранирует HTML-спецсимволы, чтобы текст можно было безопасно отрисовать через v-html
// (используется перед highlightSearchText для произвольного пользовательского текста).
export function escapeHtml(text: string): string {
    if (!text) {
        return text;
    }
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Убирает разметку подсветки из текста перед использованием его в логике
// (звонок/почта/ссылка/копирование). highlightSearchText оборачивает совпадения
// в <span class='searchedText'> с одинарными кавычками — именно это и зачищаем.
export function stripHighlightMarkup(text: string): string {
    if (!text) {
        return text;
    }
    return text
        .replace(/<span class='searchedText'>/g, '')
        .replace(/<\/span>/g, '');
}

// Обрезаем хвост из любой смеси пробелов, переносов и <br>; переносы внутри
// текста не трогаем.
const TRAILING_LINE_BREAKS = /(?:\s|<br\s*\/?>)+$/i;

export function trimTrailingLineBreaks(text: string): string {
    if (!text) {
        return text;
    }
    return text.replace(TRAILING_LINE_BREAKS, '');
}

export type QuotedTextSegment = {
    text: string;
    italic: boolean;
};

export function formatChangelogText(text: string): QuotedTextSegment[] {
    if (!text) {
        return [];
    }

    const normalizedText = text.replace(
        /^(\s*(?:\[[^\]]*\]\s*)?)(\p{L})/u,
        (_, prefix: string, firstLetter: string) =>
            `${prefix}${firstLetter.toLocaleLowerCase()}`
    );
    const quoteRegex = /"([^"]*)"|«([^»]*)»|“([^”]*)”/g;
    const segments: QuotedTextSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = quoteRegex.exec(normalizedText)) !== null) {
        if (match.index > lastIndex) {
            segments.push({
                text: normalizedText.slice(lastIndex, match.index),
                italic: false,
            });
        }

        segments.push({
            text: match[1] ?? match[2] ?? match[3] ?? "",
            italic: true,
        });
        lastIndex = quoteRegex.lastIndex;
    }

    if (lastIndex < normalizedText.length) {
        segments.push({
            text: normalizedText.slice(lastIndex),
            italic: false,
        });
    }

    return segments;
}

// Плейсхолдеры, которые бэкенд оставляет в тексте сообщений чата (методы HandleLinks/HandleBills):
//   {{L_i-n*k$:<индекс в Links>*  — ссылка на карточку клиента или проекта
//   #bill/<Id счёта>              — ссылка на карточку счёта
const CHAT_LINK_TOKEN_PREFIX = "{{L_i-n*k$:";
const CHAT_BILL_TOKEN_PREFIX = "#bill/";
const CHAT_PLACEHOLDER_REGEX = /\{\{L_i-n\*k\$:(\d+)\*|#bill\/(\d+)/g;

function chatLinkPage(type: number): 'client' | 'project' | null {
    if (type === ChatLinkType.Client) {
        return 'client';
    }
    if (type === ChatLinkType.Project) {
        return 'project';
    }
    return null;
}

function chatBillLabel(bill: Bill): string {
    const number = bill.Number ? String(i18n.global.t('bill_number_prefix', { number: bill.Number })) + ' ' : '';
    const date = moment(bill.Date);
    const dateText = bill.Date && date.isValid() ? String(i18n.global.t('date_from_prefix')) + ' ' + date.format('DD.MM.YYYY') : '';
    return (String(i18n.global.t('bill_word')) + ' ' + number + dateText).trim();
}

// Подставляет вместо плейсхолдеров ссылки на клиента/проект и счёт.
// plainText — для мест, где текст выводится интерполяцией, а не через v-html.
export function applyChatMessagePlaceholders(
    text: string,
    links?: Link[] | null,
    bills?: Bill[] | null,
    options?: { plainText?: boolean }
): string {
    if (!text) {
        return text;
    }
    if (!text.includes(CHAT_LINK_TOKEN_PREFIX) && !text.includes(CHAT_BILL_TOKEN_PREFIX)) {
        return text;
    }

    const plainText = options?.plainText === true;
    const billsById = new Map<number, Bill>((bills ?? []).map(bill => [bill.Id, bill]));

    return text.replace(CHAT_PLACEHOLDER_REGEX, (match, linkIndex?: string, billId?: string) => {
        if (linkIndex !== undefined) {
            const link = links?.[Number(linkIndex)];
            const page = link ? chatLinkPage(link.Type) : null;
            // данных по ссылке нет — вырезаем токен, чтобы не показывать его пользователю
            if (!link || !page) {
                return '';
            }
            const name = link.Name ?? '';
            if (plainText) {
                return name;
            }
            return `<a class="link-message chat-entity-link" href="/main?page=${page}&amp;id=${link.Id}"`
                + ` data-entity-page="${page}" data-entity-id="${link.Id}">${escapeHtml(name)}</a>`;
        }

        const bill = billsById.get(Number(billId));
        // такого счёта в сообщении нет — оставляем подстроку как есть, это может быть обычный текст
        if (!bill) {
            return match;
        }
        const label = chatBillLabel(bill);
        if (plainText) {
            return label;
        }
        return `<span class="link-message chat-bill-link" data-bill-id="${bill.Id}">${escapeHtml(label)}</span>`;
    });
}

const JOINED_USERS_MESSAGE_PREFIXES = [
    'Следующие пользователи присоединились к беседе',
    'The following users joined the conversation',
    'Những người dùng sau đã đừơc thêm vào cuộc trò chuyện'
];

// Ключ для сравнения фраз: снимаем диакритику и приводим регистр/пробелы.
// Вьетнамский текст с бэка приходит то в NFC, то в NFD, а тона расставлены
// неустойчиво (đừơc / được — разные последовательности символов), поэтому
// сравнивать сырые строки нельзя.
function phraseMatchKey(text: string): string {
    return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/gi, 'd')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

// "<фраза>:\n<имя>\n<имя>\n" -> "<фраза>: <имя>, <имя>", чтобы такое сообщение
// помещалось в одну строку превью. Остальные сообщения возвращаются без изменений.
export function formatJoinedUsersMessage(message: string): string {
    if (!message) {
        return message;
    }

    const [firstLine, ...rest] = message.split('\n');
    const heading = firstLine.trim().replace(/:\s*$/, '');
    const headingKey = phraseMatchKey(heading);

    const isJoinedUsersMessage = JOINED_USERS_MESSAGE_PREFIXES
        .some(prefix => headingKey.startsWith(phraseMatchKey(prefix)));

    if (!isJoinedUsersMessage) {
        return message;
    }

    const users = rest.map(line => line.trim()).filter(Boolean);

    if (!users.length) {
        return heading;
    }

    return `${heading}: ${users.join(', ')}`;
}

// Принимает число и массив вариантов сопутствующих слов в разных склонениях
// Пример: (3, ['сообщение', 'сообщения', 'сообщений'])
export function declOfNum(number: number, titles: string[]): string {
    if (number === 0) {
        return titles[2];
    }

    const cases = [2, 0, 1, 1, 1, 2];
    return titles[
        (number % 100 > 4 && number % 100 < 20)
            ? 2
            : cases[(number % 10 < 5) ? number % 10 : 5]
        ];
}
